/**
 * English as Second Language (ESL) Student Notes - Years 11-13
 * Vanuatu National Syllabus
 * Last Updated: February 2026
 */

// ============================================
// IMAGE RESOURCES FOR ESL NOTES
// ============================================
export const eslImages = {
  literature: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800",
  reading: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800",
  writing: "https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800",
  speaking: "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800",
  books: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800",
  classroom: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=800",
  essay: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800",
  poetry: "https://images.unsplash.com/photo-1473186505569-9c61870c11f9?w=800"
};

// ==================== YEAR 11 ESL NOTES ====================
export const year11ESLStudentNotes = {
  strand1: {
    title: "Literature",
    overview: "Understanding and analysing various forms of visual, oral, and written texts including their elements, style, and meaning.",
    image: eslImages.literature,
    units: [
      {
        id: "1.1",
        title: "Forms and Elements of Literature",
        image: eslImages.books,
        objectives: [
          "Identify various forms of texts (film, novels, drama, short story, poetry)",
          "Identify elements of texts such as sentence structures and word choice",
          "Identify author, director, poet, and playwright correctly",
          "State the theme of a selected text",
          "Describe features of different types of texts"
        ],
        keyTerms: [
          { term: "Theme", definition: "The central idea or message of a literary work. It is the underlying meaning that the author wants to convey." },
          { term: "Tone", definition: "The author's attitude toward the subject matter or audience, conveyed through word choice and style." },
          { term: "Setting", definition: "The time and place in which a story takes place. It can include physical location, time period, and social environment." },
          { term: "Character", definition: "A person, animal, or figure in a literary work. Characters can be protagonists (main) or antagonists (opposing)." },
          { term: "Conflict", definition: "The struggle between opposing forces in a story. Can be internal (within a character) or external (with outside forces)." },
          { term: "Narrative Structure", definition: "The organization of a story including exposition, rising action, climax, falling action, and resolution." },
          { term: "Imagery", definition: "Descriptive language that appeals to the senses (sight, sound, smell, taste, touch) to create mental pictures." },
          { term: "Symbolism", definition: "The use of objects, characters, or events to represent ideas or concepts beyond their literal meaning." }
        ],
        studyNotes: `
## Forms of Literary Texts

### 1. Novel
- Extended work of fiction
- Complex plot and character development
- Examples: "To Kill a Mockingbird", "Things Fall Apart"

### 2. Short Story
- Brief fictional narrative
- Single plot with limited characters
- Can be read in one sitting

### 3. Drama/Play
- Written for stage performance
- Dialogue and stage directions
- Examples: Shakespeare's plays

### 4. Poetry
- Uses rhythm, meter, and imagery
- Condensed language with emotional impact
- Types: sonnets, free verse, haiku

### 5. Film
- Visual storytelling medium
- Combines dialogue, visuals, music
- Uses camera techniques and editing

## Elements of Style

### Sentence Structure
- Simple sentences: One independent clause
- Compound sentences: Two independent clauses joined by conjunction
- Complex sentences: Independent clause + dependent clause

### Word Choice (Diction)
- Formal vs. informal language
- Connotation vs. denotation
- Technical vs. everyday vocabulary

## Analysing Texts

### Key Questions to Ask:
1. What is the main theme or message?
2. Who are the main characters and how do they develop?
3. What is the setting and how does it affect the story?
4. What is the conflict and how is it resolved?
5. What literary devices does the author use?
        `,
        practiceQuestions: [
          "Name THREE different forms of literary texts and give an example of each.",
          "Define the term 'theme' and give an example from a text you have studied.",
          "Explain the difference between a simple sentence and a complex sentence.",
          "What is the difference between the protagonist and antagonist in a story?",
          "Describe the five parts of narrative structure.",
          "How does setting affect the mood of a story?"
        ],
        examTips: [
          "Always support your analysis with specific evidence (quotes) from the text",
          "Know the difference between theme, subject, and topic",
          "Practice identifying literary devices in different text types"
        ]
      },
      {
        id: "1.2",
        title: "Literary Analysis and Interpretation",
        image: eslImages.poetry,
        objectives: [
          "Explain how the author uses various forms and elements of style to convey meaning",
          "Analyse how imagery has been used to achieve its purpose",
          "Use specific evidence from texts to support interpretations",
          "Evaluate the effectiveness of writing elements"
        ],
        keyTerms: [
          { term: "Analysis", definition: "The process of breaking down a text into its component parts to understand how they work together." },
          { term: "Interpretation", definition: "Explaining the meaning of a text based on evidence and your understanding of literary elements." },
          { term: "Evidence", definition: "Specific quotes or examples from the text that support your analysis or argument." },
          { term: "Figurative Language", definition: "Language that uses figures of speech like metaphors, similes, and personification to create effects." },
          { term: "Metaphor", definition: "A comparison between two unlike things without using 'like' or 'as' (e.g., 'Life is a journey')." },
          { term: "Simile", definition: "A comparison between two unlike things using 'like' or 'as' (e.g., 'She runs like the wind')." },
          { term: "Personification", definition: "Giving human qualities to non-human things (e.g., 'The wind whispered through the trees')." }
        ],
        studyNotes: `
## How to Analyse Literary Texts

### Step 1: Read Carefully
- Read the text multiple times
- Note first impressions and reactions
- Identify main ideas and themes

### Step 2: Identify Literary Elements
- Setting: When and where?
- Characters: Who are they? How do they change?
- Plot: What happens? What is the conflict?
- Theme: What is the message?

### Step 3: Examine Language and Style
- Word choice: Why did the author choose these words?
- Figurative language: What metaphors, similes are used?
- Tone: What is the author's attitude?

### Step 4: Support with Evidence
- Use specific quotes from the text
- Explain how the quote supports your point
- Use the PEE method:
  * **P**oint: State your argument
  * **E**vidence: Provide a quote
  * **E**xplanation: Explain how the quote supports your point

## Sound Devices in Poetry

### Alliteration
- Repetition of initial consonant sounds
- Example: "Peter Piper picked a peck"

### Assonance
- Repetition of vowel sounds
- Example: "The rain in Spain falls mainly on the plain"

### Onomatopoeia
- Words that imitate sounds
- Examples: buzz, splash, crash, whisper
        `,
        practiceQuestions: [
          "What is the difference between a metaphor and a simile? Give an example of each.",
          "Explain the PEE method for writing literary analysis.",
          "How does imagery help an author convey meaning? Give an example.",
          "What is personification? Write a sentence using personification.",
          "Why is it important to use evidence from the text when analysing literature?",
          "Identify TWO sound devices and explain how they create effect."
        ],
        examTips: [
          "Always use the PEE (Point, Evidence, Explanation) structure",
          "Include specific quotes in your analysis",
          "Explain WHY the author made certain choices, not just WHAT they did"
        ]
      }
    ]
  },
  strand2: {
    title: "Reading",
    overview: "Developing reading comprehension skills for informative, argumentative, persuasive, and narrative texts.",
    image: eslImages.reading,
    units: [
      {
        id: "2.1",
        title: "Reading Comprehension Strategies",
        image: eslImages.books,
        objectives: [
          "Use different reading strategies to understand text",
          "Skim read to identify main ideas",
          "Scan a text for specific information",
          "Summarize the main ideas in a text",
          "Distinguish fact from opinion"
        ],
        keyTerms: [
          { term: "Skimming", definition: "Reading quickly to get the general idea or gist of a text without reading every word." },
          { term: "Scanning", definition: "Moving your eyes quickly over a text to find specific information like names, dates, or key words." },
          { term: "Main Idea", definition: "The central point or most important thought in a paragraph or text." },
          { term: "Supporting Details", definition: "Information that explains, proves, or expands on the main idea." },
          { term: "Fact", definition: "A statement that can be proven true or false with evidence." },
          { term: "Opinion", definition: "A personal belief, view, or judgment that cannot be proven true or false." },
          { term: "Inference", definition: "A conclusion drawn from evidence and reasoning rather than from explicit statements." }
        ],
        studyNotes: `
## Reading Strategies

### 1. Skimming
**Purpose:** Get the general idea quickly

**How to Skim:**
- Read the title, headings, and subheadings
- Read the first and last paragraphs
- Look at pictures, graphs, and captions
- Read the first sentence of each paragraph

### 2. Scanning
**Purpose:** Find specific information

**How to Scan:**
- Know what you're looking for
- Move your eyes quickly over the text
- Look for key words, numbers, names
- Don't read every word

### 3. Close Reading
**Purpose:** Deep understanding

**How to Close Read:**
- Read slowly and carefully
- Re-read difficult sections
- Take notes and annotate
- Ask questions as you read

## Finding Main Ideas

### The Main Idea:
- Usually found in the first or last sentence of a paragraph
- Answers the question: "What is this paragraph about?"
- All other sentences support this idea

### Supporting Details:
- Examples and illustrations
- Facts and statistics
- Reasons and explanations
- Descriptions

## Fact vs. Opinion

| Fact | Opinion |
|------|---------|
| Can be verified | Cannot be proven |
| Uses specific data | Uses words like "I think" |
| Objective | Subjective |
| Example: "Vanuatu has 83 islands" | Example: "Vanuatu is the most beautiful country" |
        `,
        practiceQuestions: [
          "Explain the difference between skimming and scanning.",
          "When would you use skimming? Give an example.",
          "How do you identify the main idea of a paragraph?",
          "What is the difference between a fact and an opinion?",
          "Identify the main idea: 'Climate change is affecting Pacific islands. Sea levels are rising, causing coastal erosion. Many communities are losing their homes.'",
          "Is this a fact or opinion? 'The movie received excellent reviews from critics.'"
        ],
        examTips: [
          "Practice skimming and scanning with timed exercises",
          "Look for signal words like 'most importantly', 'in conclusion'",
          "Watch for opinion words: 'I believe', 'in my view', 'should'"
        ]
      },
      {
        id: "2.2",
        title: "Reading Different Text Types",
        image: eslImages.classroom,
        objectives: [
          "Identify different types of texts and their purposes",
          "Understand informative, argumentative, and persuasive texts",
          "Analyse the structure and language of different text types",
          "Evaluate the effectiveness of texts"
        ],
        keyTerms: [
          { term: "Informative Text", definition: "Text that provides information or explains a topic. Examples: news articles, encyclopedias, textbooks." },
          { term: "Argumentative Text", definition: "Text that presents a position on an issue and uses evidence and reasoning to support it." },
          { term: "Persuasive Text", definition: "Text that tries to convince the reader to agree with a viewpoint or take action." },
          { term: "Narrative Text", definition: "Text that tells a story, either fictional or non-fictional, with characters and events." },
          { term: "Audience", definition: "The intended readers or viewers of a text." },
          { term: "Purpose", definition: "The reason why a text was written: to inform, persuade, entertain, or instruct." }
        ],
        studyNotes: `
## Types of Texts

### 1. Informative Texts
**Purpose:** To inform or explain

**Features:**
- Factual information
- Objective language
- Clear structure with headings
- May include diagrams, charts

**Examples:** News reports, textbooks, reports

### 2. Argumentative Texts
**Purpose:** To present and defend a position

**Structure:**
1. Introduction with thesis statement
2. Arguments with evidence
3. Counter-arguments (rebuttal)
4. Conclusion

**Features:**
- Logical reasoning
- Evidence and examples
- Signal words: "therefore", "however", "consequently"

### 3. Persuasive Texts
**Purpose:** To convince the reader

**Techniques:**
- Emotional appeals
- Rhetorical questions
- Expert opinions
- Statistics and facts
- Emotive language

**Examples:** Advertisements, speeches, editorials

### 4. Narrative Texts
**Purpose:** To tell a story

**Features:**
- Characters and setting
- Plot with conflict
- Beginning, middle, end
- May have a moral or message

## Analysing Text Purpose

### Questions to Ask:
1. What type of text is this?
2. Who is the intended audience?
3. What is the author trying to achieve?
4. What techniques are used?
5. Is the text effective? Why or why not?
        `,
        practiceQuestions: [
          "What is the difference between argumentative and persuasive texts?",
          "List THREE features of informative texts.",
          "What is the purpose of a narrative text?",
          "Identify the audience and purpose of an advertisement for a new smartphone.",
          "What techniques might a persuasive text use to convince readers?",
          "Explain the structure of an argumentative essay."
        ],
        examTips: [
          "Identify the text type before analysing it",
          "Consider who the audience is and why",
          "Look for specific language features that indicate purpose"
        ]
      }
    ]
  },
  strand3: {
    title: "Writing",
    overview: "Developing writing skills for various purposes including critical, persuasive, creative, and practical writing.",
    image: eslImages.writing,
    units: [
      {
        id: "3.1",
        title: "Language Tools",
        image: eslImages.essay,
        objectives: [
          "Understand the grammatical class of words (parts of speech)",
          "Use punctuation correctly to achieve clarity",
          "Understand and use figures of speech",
          "Construct grammatically correct sentences"
        ],
        keyTerms: [
          { term: "Noun", definition: "A word that names a person, place, thing, or idea. Examples: teacher, Vanuatu, book, happiness." },
          { term: "Verb", definition: "A word that expresses an action, occurrence, or state of being. Examples: run, think, is." },
          { term: "Adjective", definition: "A word that describes or modifies a noun. Examples: beautiful, tall, three." },
          { term: "Adverb", definition: "A word that modifies a verb, adjective, or other adverb. Examples: quickly, very, often." },
          { term: "Pronoun", definition: "A word that takes the place of a noun. Examples: he, she, it, they." },
          { term: "Preposition", definition: "A word that shows relationship between a noun/pronoun and other words. Examples: in, on, under, between." },
          { term: "Conjunction", definition: "A word that connects words, phrases, or clauses. Examples: and, but, or, because." },
          { term: "Connotation", definition: "The implied or associated meaning of a word beyond its literal definition." },
          { term: "Denotation", definition: "The literal, dictionary definition of a word." }
        ],
        studyNotes: `
## Parts of Speech

### 1. Nouns
- **Common nouns:** general names (city, book, teacher)
- **Proper nouns:** specific names (Port Vila, Bible, Mr. Smith)
- **Abstract nouns:** ideas/concepts (love, freedom, knowledge)
- **Collective nouns:** groups (team, flock, family)

### 2. Verbs
- **Action verbs:** show action (run, write, speak)
- **Linking verbs:** connect subject to description (is, seems, appears)
- **Helping verbs:** help main verbs (have, will, can)

### 3. Adjectives
- Describe nouns
- Answer: Which one? What kind? How many?
- Example: "The tall, green tree swayed in the strong wind."

### 4. Adverbs
- Modify verbs, adjectives, or other adverbs
- Often end in -ly
- Answer: How? When? Where? To what extent?
- Example: "She spoke quietly but very clearly."

## Punctuation

| Mark | Name | Use |
|------|------|-----|
| . | Period/Full stop | End of sentence |
| , | Comma | Separate items, clauses |
| ? | Question mark | End of question |
| ! | Exclamation mark | Strong emotion |
| ' | Apostrophe | Contractions, possession |
| " " | Quotation marks | Direct speech, titles |
| : | Colon | Introduce list or explanation |
| ; | Semicolon | Join related sentences |

## Sentence Types

### By Structure:
1. **Simple:** One independent clause
   - "The dog barked."
   
2. **Compound:** Two independent clauses
   - "The dog barked, and the cat ran away."
   
3. **Complex:** Independent + dependent clause
   - "Although it was raining, we went outside."
   
4. **Compound-Complex:** Combination
   - "When the bell rang, the students left, and the teacher cleaned up."
        `,
        practiceQuestions: [
          "Identify the parts of speech in this sentence: 'The young student quickly completed her assignment.'",
          "What is the difference between a common noun and a proper noun?",
          "Write a sentence using a compound structure.",
          "What is the difference between connotation and denotation?",
          "When do you use a semicolon?",
          "Identify the adjectives and adverbs in: 'The extremely talented musician played beautifully.'"
        ],
        examTips: [
          "Know all eight parts of speech and their functions",
          "Practice identifying parts of speech in sentences",
          "Use varied sentence structures in your writing"
        ]
      },
      {
        id: "3.2",
        title: "Essay Writing",
        image: eslImages.writing,
        objectives: [
          "Understand the essay writing process",
          "Organise ideas using introduction, body, and conclusion",
          "Use evidence and examples to support arguments",
          "Edit and proofread written work"
        ],
        keyTerms: [
          { term: "Thesis Statement", definition: "A sentence that states the main argument or point of your essay, usually at the end of the introduction." },
          { term: "Topic Sentence", definition: "The first sentence of a paragraph that states the main idea of that paragraph." },
          { term: "Supporting Evidence", definition: "Facts, examples, quotes, or statistics that support your argument." },
          { term: "Transition Words", definition: "Words or phrases that connect ideas and show relationships between sentences/paragraphs." },
          { term: "Draft", definition: "A preliminary version of a piece of writing that will be revised and edited." },
          { term: "Proofread", definition: "Reading through a text to find and correct errors in spelling, grammar, and punctuation." }
        ],
        studyNotes: `
## The Essay Writing Process

### 1. Planning/Prewriting
- Brainstorm ideas
- Research your topic
- Create an outline
- Decide on your thesis

### 2. Drafting
- Write your first draft
- Focus on ideas, not perfection
- Follow your outline
- Include evidence and examples

### 3. Revising
- Review content and organisation
- Add or remove information
- Improve clarity and flow
- Strengthen arguments

### 4. Editing/Proofreading
- Check spelling and grammar
- Fix punctuation errors
- Check sentence structure
- Ensure consistency

## Essay Structure

### Introduction
1. Hook: Grab the reader's attention
2. Background: Provide context
3. Thesis: State your main argument

### Body Paragraphs (PEEL)
- **P**oint: Topic sentence stating main idea
- **E**vidence: Supporting quote or example
- **E**xplanation: Explain how evidence supports point
- **L**ink: Connect to thesis or next paragraph

### Conclusion
1. Restate thesis (in different words)
2. Summarize main points
3. Final thought or call to action

## Transition Words

| Purpose | Examples |
|---------|----------|
| Adding | Furthermore, Moreover, In addition |
| Contrasting | However, On the other hand, Nevertheless |
| Cause/Effect | Therefore, Consequently, As a result |
| Sequence | First, Next, Finally |
| Example | For example, For instance, Such as |
| Conclusion | In conclusion, To summarize, Overall |
        `,
        practiceQuestions: [
          "What are the four stages of the essay writing process?",
          "What is a thesis statement and where should it appear?",
          "Explain the PEEL structure for body paragraphs.",
          "What should an introduction include?",
          "Give THREE examples of transition words for contrasting ideas.",
          "Why is it important to proofread your essay?"
        ],
        examTips: [
          "Always plan your essay before writing",
          "Make sure your thesis is clear and specific",
          "Use transition words to connect your ideas",
          "Leave time to proofread your work"
        ]
      }
    ]
  },
  strand4: {
    title: "Listening and Speaking",
    overview: "Developing aural comprehension and oral communication skills for various contexts.",
    image: eslImages.speaking,
    units: [
      {
        id: "4.1",
        title: "Listening Skills",
        image: eslImages.classroom,
        objectives: [
          "Answer questions after listening to an aural text",
          "List key ideas from a live presentation",
          "Ask questions after listening to a speaker",
          "Summarize ideas from an aural text"
        ],
        keyTerms: [
          { term: "Aural", definition: "Related to hearing or listening. An aural text is one that is heard rather than read." },
          { term: "Active Listening", definition: "Fully concentrating on what is being said, understanding the message, and responding thoughtfully." },
          { term: "Note-taking", definition: "Writing down key information while listening to help remember and understand content." },
          { term: "Inference", definition: "Understanding implied meaning that is not directly stated." },
          { term: "Paraphrase", definition: "Restating information in your own words while keeping the same meaning." }
        ],
        studyNotes: `
## Active Listening Skills

### Before Listening
- Prepare: Know the topic if possible
- Focus: Remove distractions
- Predict: What might you hear?

### During Listening
- Concentrate: Focus on the speaker
- Note-take: Write key points
- Don't interrupt: Wait for your turn
- Listen for main ideas and details

### After Listening
- Summarize: What were the main points?
- Clarify: Ask questions if unsure
- Reflect: What did you learn?

## Note-Taking Strategies

### 1. Keywords Method
- Write only important words
- Use symbols and abbreviations
- Leave space for additions

### 2. Cornell Method
| Notes | Cues |
|-------|------|
| Main ideas and details | Key words |
| During lecture | After lecture |

**Summary:** Write a brief summary at the bottom

### 3. Mind Mapping
- Central topic in the middle
- Branch out with main ideas
- Add details to branches

## Asking Good Questions

### Types of Questions:
1. **Clarification:** "Could you explain...?"
2. **Detail:** "Can you give an example of...?"
3. **Opinion:** "What do you think about...?"
4. **Extension:** "What happens next...?"

### Question Starters:
- What...?
- Why...?
- How...?
- Can you explain...?
- What do you mean by...?
        `,
        practiceQuestions: [
          "What are the three stages of active listening?",
          "Why is note-taking important when listening?",
          "What is the Cornell note-taking method?",
          "Give TWO examples of clarification questions.",
          "How can you prepare before listening to a presentation?",
          "What should you do if you don't understand something a speaker says?"
        ],
        examTips: [
          "Practice listening to English podcasts or news",
          "Focus on main ideas first, then details",
          "Don't try to write everything - capture key points"
        ]
      },
      {
        id: "4.2",
        title: "Speaking Skills",
        image: eslImages.speaking,
        objectives: [
          "Use clear descriptions of events or places",
          "Express views clearly in group discussions",
          "Give a talk within allocated time",
          "Prepare and deliver a public speech"
        ],
        keyTerms: [
          { term: "Fluency", definition: "The ability to speak smoothly and confidently without too many pauses or hesitations." },
          { term: "Accuracy", definition: "Using correct grammar, pronunciation, and vocabulary when speaking." },
          { term: "Enunciation", definition: "Clear pronunciation of words, making each sound distinct." },
          { term: "Intonation", definition: "The rise and fall of voice pitch when speaking, which conveys meaning and emotion." },
          { term: "Body Language", definition: "Non-verbal communication through posture, gestures, facial expressions, and eye contact." }
        ],
        studyNotes: `
## Public Speaking Skills

### Preparation
1. Know your topic well
2. Organise your ideas logically
3. Practice multiple times
4. Prepare visual aids if needed
5. Time yourself

### Delivery

**Voice:**
- Speak clearly and loudly enough
- Vary your pace and tone
- Use pauses for emphasis
- Avoid filler words (um, uh, like)

**Body Language:**
- Make eye contact with audience
- Stand confidently
- Use natural gestures
- Smile when appropriate
- Move purposefully

### Structure of a Speech

1. **Opening:** Hook your audience
   - Start with a question
   - Tell a story
   - Share a surprising fact

2. **Body:** Present your main points
   - 2-3 main ideas
   - Support with examples
   - Use transitions

3. **Closing:** End memorably
   - Summarize key points
   - Call to action
   - Memorable final statement

## Group Discussion Skills

### Do:
- Listen to others
- Build on others' ideas
- Stay on topic
- Support your opinions with reasons
- Encourage quieter members

### Don't:
- Interrupt others
- Dominate the conversation
- Go off-topic
- Criticize others personally
- Speak too softly
        `,
        practiceQuestions: [
          "What are THREE ways to improve fluency in speaking?",
          "Why is body language important in public speaking?",
          "How should you structure a speech?",
          "What are some good ways to open a speech?",
          "List THREE do's and THREE don'ts for group discussions.",
          "What is the difference between fluency and accuracy?"
        ],
        examTips: [
          "Practice speaking English daily, even by yourself",
          "Record yourself and listen for areas to improve",
          "Watch how confident speakers use body language"
        ]
      }
    ]
  }
};

// ==================== YEAR 12 ESL NOTES ====================
export const year12ESLStudentNotes = {
  strand1: {
    title: "Literature",
    overview: "Advanced literary analysis exploring relationships between features, events, and meaning in texts.",
    image: eslImages.literature,
    units: [
      {
        id: "1.1",
        title: "Advanced Literary Analysis",
        image: eslImages.books,
        objectives: [
          "Identify key elements of style in a text",
          "Explain the relationships between literary features and events",
          "Analyse how language features create voice and purpose",
          "Compare and contrast writers from the same time period"
        ],
        keyTerms: [
          { term: "Voice", definition: "The distinctive style or personality that comes through in a piece of writing." },
          { term: "Rhetorical Device", definition: "Techniques used by writers to persuade or create effects: repetition, rhetorical questions, parallelism." },
          { term: "Mood", definition: "The emotional atmosphere of a text created through setting, imagery, and word choice." },
          { term: "Atmosphere", definition: "The overall feeling or ambiance created in a literary work through description and language." },
          { term: "Point of View", definition: "The perspective from which a story is told: first person, third person limited, third person omniscient." },
          { term: "Intensive Study", definition: "Deep, detailed analysis of a single text focusing on all its literary elements." }
        ],
        studyNotes: `
## Analysing Voice in Literature

### What Creates Voice?
1. **Word Choice:** Formal vs. informal, simple vs. complex
2. **Sentence Structure:** Long flowing sentences vs. short punchy ones
3. **Tone:** Serious, humorous, sarcastic, thoughtful
4. **Perspective:** First person, third person, omniscient

### Points of View

| Type | Description | Example |
|------|-------------|---------|
| First Person | Narrator is a character using "I" | "I walked into the room and saw..." |
| Third Person Limited | Focus on one character | "She felt nervous as she..." |
| Third Person Omniscient | All-knowing narrator | "John thought..., while Mary believed..." |

## Rhetorical and Literary Devices

### Rhetorical Devices:
- **Repetition:** Repeating words for emphasis
- **Rhetorical Question:** Question without expected answer
- **Parallelism:** Similar structure in phrases
- **Anaphora:** Repetition at beginning of sentences

### Literary Devices:
- **Foreshadowing:** Hints about future events
- **Flashback:** Scene from the past
- **Irony:** Contrast between expectation and reality
- **Allusion:** Reference to another text or event

## Comparing Writers

### Elements to Compare:
1. **Themes:** What topics do they explore?
2. **Style:** How do they write? (formal, poetic, etc.)
3. **Techniques:** What literary devices do they use?
4. **Context:** How does their time period influence them?
5. **Perspective:** What viewpoints do they present?
        `,
        practiceQuestions: [
          "What elements contribute to an author's voice?",
          "Explain the difference between first person and third person point of view.",
          "What is a rhetorical question and why might an author use one?",
          "Define irony and give an example from a text you have studied.",
          "What elements should you compare when analysing two writers from the same period?",
          "How does word choice affect the tone of a text?"
        ],
        examTips: [
          "When comparing writers, use a clear structure (point by point or writer by writer)",
          "Always include specific quotes to support your analysis",
          "Consider how context (time, place, society) influences writing"
        ]
      }
    ]
  },
  strand2: {
    title: "Reading",
    overview: "Critical reading of less familiar and complex texts with various communicative purposes.",
    image: eslImages.reading,
    units: [
      {
        id: "2.1",
        title: "Critical Reading Skills",
        image: eslImages.books,
        objectives: [
          "Identify genre, theme, purpose, and target audience",
          "Analyse language use, structure, and layout",
          "Evaluate how language conveys key messages",
          "Assess the validity of arguments"
        ],
        keyTerms: [
          { term: "Genre", definition: "A category of text defined by style, form, or content: fiction, non-fiction, poetry, drama." },
          { term: "Bias", definition: "A tendency to favour one perspective over others, often affecting objectivity." },
          { term: "Credibility", definition: "The quality of being believable and trustworthy." },
          { term: "Validity", definition: "The extent to which an argument or conclusion is logically sound and well-supported." },
          { term: "Rhetorical Purpose", definition: "The intended effect of a text on its audience: to inform, persuade, entertain, or provoke." }
        ],
        studyNotes: `
## Evaluating Arguments

### Is the Argument Valid?

**Check for:**
1. **Clear Thesis:** Is the main claim stated clearly?
2. **Strong Evidence:** Are facts, examples, statistics used?
3. **Logical Reasoning:** Does the evidence support the claim?
4. **Counter-arguments:** Are opposing views addressed?
5. **Credible Sources:** Are sources reliable and relevant?

### Common Logical Fallacies

| Fallacy | Description | Example |
|---------|-------------|---------|
| Ad Hominem | Attacking the person, not the argument | "You can't trust his research because he's young." |
| Straw Man | Misrepresenting someone's argument | "They want environmental laws, so they must hate business." |
| False Dilemma | Presenting only two options when more exist | "You're either with us or against us." |
| Appeal to Authority | Using an unqualified "expert" | "A celebrity says this product works..." |
| Hasty Generalization | Drawing conclusions from insufficient evidence | "I met two rude people from there, so everyone must be rude." |

## Analysing Language Use

### Questions to Consider:
1. What type of vocabulary is used? (technical, emotive, formal)
2. What sentence structures dominate? (simple, complex, varied)
3. What rhetorical devices are employed?
4. How does language reflect purpose and audience?

### Language and Purpose

| Purpose | Language Features |
|---------|-------------------|
| Inform | Formal, objective, factual, technical terms |
| Persuade | Emotive, inclusive pronouns, rhetorical questions |
| Entertain | Varied, descriptive, dialogue, humour |
| Argue | Logical connectors, evidence-based, formal |
        `,
        practiceQuestions: [
          "What makes an argument valid?",
          "Identify and explain TWO common logical fallacies.",
          "How can you tell if a source is credible?",
          "What language features would you expect in a persuasive text?",
          "Why is it important to consider an author's potential bias?",
          "How does understanding the target audience help you analyse a text?"
        ],
        examTips: [
          "Look for evidence of bias in the language used",
          "Identify logical fallacies to evaluate arguments",
          "Consider who the text was written for and why"
        ]
      }
    ]
  },
  strand3: {
    title: "Writing",
    overview: "Advanced writing including 800-word essays, formal persuasive writing, and research reports.",
    image: eslImages.writing,
    units: [
      {
        id: "3.1",
        title: "Advanced Essay Writing",
        image: eslImages.essay,
        objectives: [
          "Use prewriting techniques to generate ideas",
          "Develop drafts through feedback and revision",
          "Write an 800-word essay demonstrating critical thinking",
          "Use expression and style appropriately"
        ],
        keyTerms: [
          { term: "Prewriting", definition: "Activities done before drafting: brainstorming, mind mapping, freewriting, outlining." },
          { term: "Revision", definition: "The process of making significant changes to content, structure, and clarity of writing." },
          { term: "Coherence", definition: "The logical connection between ideas in writing, achieved through clear organisation and transitions." },
          { term: "Register", definition: "The level of formality in language, varying from informal to formal depending on context." }
        ],
        studyNotes: `
## Prewriting Techniques

### 1. Brainstorming
- List all ideas without judging
- Group related ideas
- Select best ideas to develop

### 2. Freewriting
- Write continuously for 10-15 minutes
- Don't stop to correct or edit
- Find ideas in the flow of writing

### 3. Mind Mapping
- Central topic in the middle
- Branch out with related ideas
- Connect ideas with lines

### 4. Questioning
- Who? What? When? Where? Why? How?
- Generate ideas by answering questions

## Writing an 800-Word Essay

### Structure:
- **Introduction (100-150 words):** Hook, context, thesis
- **Body (500-600 words):** 3-4 paragraphs, each with one main point
- **Conclusion (100-150 words):** Restate thesis, summarize, final thought

### Planning Time:
- 10 mins: Planning and outline
- 30 mins: Drafting
- 10 mins: Revising and editing

### Paragraph Development

**PEEL Structure:**
- **P**oint: State your main idea
- **E**vidence: Provide supporting quote/example
- **E**xplanation: Explain how evidence supports point
- **L**ink: Connect to thesis or next paragraph

## Revision Checklist

### Content:
- [ ] Is my thesis clear and specific?
- [ ] Does each paragraph have one main idea?
- [ ] Have I provided enough evidence?
- [ ] Are my arguments logical?

### Organisation:
- [ ] Is my introduction engaging?
- [ ] Do my paragraphs flow logically?
- [ ] Have I used transitions effectively?
- [ ] Is my conclusion satisfying?

### Language:
- [ ] Have I varied my sentence structure?
- [ ] Is my vocabulary appropriate?
- [ ] Is my tone consistent?
- [ ] Have I checked grammar and spelling?
        `,
        practiceQuestions: [
          "Describe THREE prewriting techniques and when you might use each.",
          "How should an 800-word essay be structured?",
          "What is the PEEL structure for paragraph writing?",
          "Why is revision different from editing?",
          "What should you check for when revising your essay?",
          "How can you ensure coherence in your writing?"
        ],
        examTips: [
          "Always spend time planning before writing",
          "Use the PEEL structure for each body paragraph",
          "Leave 10 minutes at the end for revision"
        ]
      }
    ]
  },
  strand4: {
    title: "Listening and Speaking",
    overview: "Advanced listening comprehension and oral presentation skills including debates.",
    image: eslImages.speaking,
    units: [
      {
        id: "4.1",
        title: "Advanced Oral Presentation",
        image: eslImages.speaking,
        objectives: [
          "Research and present findings to an audience",
          "Use features of public speaking effectively",
          "Participate in debates and discussions",
          "Use appropriate body language and voice intonation"
        ],
        keyTerms: [
          { term: "Debate", definition: "A formal discussion where opposing arguments are presented on a topic." },
          { term: "Rebuttal", definition: "An argument that counters or disproves an opponent's point." },
          { term: "Proposition", definition: "The team in a debate arguing FOR the topic/motion." },
          { term: "Opposition", definition: "The team in a debate arguing AGAINST the topic/motion." },
          { term: "Visual Aids", definition: "Materials like slides, charts, or images that support a presentation." }
        ],
        studyNotes: `
## Debate Skills

### Structure of a Debate
1. **First Proposition Speaker:** Defines topic, presents main arguments
2. **First Opposition Speaker:** Refutes proposition, presents counter-arguments
3. **Second Proposition Speaker:** Rebuilds case, adds arguments
4. **Second Opposition Speaker:** Strengthens opposition, more rebuttals
5. **Reply Speeches:** Summarize and defend position

### Key Debate Skills:
- **Logic:** Build arguments with clear reasoning
- **Evidence:** Support claims with facts
- **Rebuttal:** Identify weaknesses in opponent's arguments
- **Persuasion:** Use rhetorical techniques effectively

### Rebuttal Techniques:
1. **Directly Challenge Evidence:** "That statistic is outdated because..."
2. **Question Logic:** "That doesn't follow because..."
3. **Provide Counter-Evidence:** "In fact, research shows..."
4. **Highlight Contradictions:** "Earlier you said... but now..."

## Presentation Skills

### Using Visual Aids Effectively:
- Keep slides simple (max 6 words per line)
- Use images, not walls of text
- Face the audience, not the screen
- Explain what you're showing
- Practice with your visuals

### Engaging Your Audience:
- Start with a question or surprising fact
- Make eye contact with different sections
- Vary your pace and volume
- Use pauses for emphasis
- Invite questions or interaction

### Handling Questions:
1. Listen carefully to the full question
2. Pause before answering
3. Answer clearly and concisely
4. If unsure, say so honestly
5. Thank the questioner
        `,
        practiceQuestions: [
          "What is the role of the first proposition speaker in a debate?",
          "Name THREE rebuttal techniques.",
          "How should you use visual aids effectively in a presentation?",
          "What makes a good debate argument?",
          "How can you engage your audience during a presentation?",
          "What should you do if you don't know the answer to a question?"
        ],
        examTips: [
          "Practice your presentation multiple times",
          "Anticipate questions and prepare answers",
          "Focus on clear structure and strong evidence"
        ]
      }
    ]
  }
};

// ==================== YEAR 13 ESL NOTES ====================
export const year13ESLStudentNotes = {
  strand1: {
    title: "Literature",
    overview: "Sophisticated literary analysis examining how design elements, style, and structure enhance text effectiveness.",
    image: eslImages.literature,
    units: [
      {
        id: "1.1",
        title: "Critical Literary Response",
        image: eslImages.poetry,
        objectives: [
          "Analyse how design elements enhance meaning and impact",
          "Reflect critically using logical reasoning in responses",
          "Discuss effects of authors' choices on readers",
          "Examine how structure produces cohesive messages"
        ],
        keyTerms: [
          { term: "Critical Response", definition: "A thoughtful, analytical reaction to a text that evaluates its effectiveness and meaning." },
          { term: "Design Elements", definition: "Visual features of a text: layout, typography, images, colour, spacing." },
          { term: "Cohesion", definition: "The linguistic links that hold a text together, including reference, conjunction, and lexical cohesion." },
          { term: "Reader Response", definition: "How different readers interpret and react to a text based on their experiences and perspectives." },
          { term: "Authorial Intent", definition: "The purpose or message the author intended to convey through their work." }
        ],
        studyNotes: `
## Writing Critical Literary Responses

### What Makes a Response "Critical"?

1. **Analysis:** Break down HOW techniques work
2. **Evaluation:** Assess effectiveness
3. **Interpretation:** Explain WHAT it means
4. **Evidence:** Support with specific quotes
5. **Perspective:** Show independent thinking

### Response Structure

**Introduction:**
- Identify text and author
- State your thesis/main argument
- Preview your analysis

**Body Paragraphs:**
- One main point per paragraph
- Evidence from the text
- Analysis of techniques
- Connection to wider meanings

**Conclusion:**
- Restate thesis with new insight
- Comment on significance
- Final reflection

## Analysing Design Elements

### Visual Elements in Texts:
- **Layout:** How information is arranged
- **White Space:** Creates emphasis and breathing room
- **Typography:** Font choices convey meaning
- **Images:** Illustrations, photographs, diagrams
- **Colour:** Emotional associations, emphasis

### Questions to Ask:
1. What visual choices has the author/designer made?
2. How do these choices affect the reader's experience?
3. Do visual elements reinforce or contrast with written content?
4. How do design elements contribute to the text's purpose?

## Discussing Reader Effects

### Consider:
- How does the text make readers FEEL?
- What reactions might the author intend?
- How might different readers respond?
- What techniques create these effects?

### Effect Examples:
| Technique | Effect |
|-----------|--------|
| Short sentences | Tension, urgency |
| Descriptive imagery | Immersion, emotion |
| Rhetorical questions | Engagement, reflection |
| Repetition | Emphasis, rhythm |
        `,
        practiceQuestions: [
          "What makes a literary response 'critical' rather than just descriptive?",
          "How can design elements enhance the meaning of a text?",
          "Explain how to structure a critical response to literature.",
          "What is 'cohesion' in a text and why is it important?",
          "How might different readers respond differently to the same text?",
          "Analyse how an author's choice of sentence length creates effect."
        ],
        examTips: [
          "Show independent thinking in your response",
          "Analyse the HOW and WHY, not just the WHAT",
          "Consider multiple interpretations of the text"
        ]
      }
    ]
  },
  strand2: {
    title: "Reading",
    overview: "Critical reading of complex, challenging texts including formal arguments and literary works.",
    image: eslImages.reading,
    units: [
      {
        id: "2.1",
        title: "Advanced Textual Analysis",
        image: eslImages.books,
        objectives: [
          "Analyse complex texts emphasizing purpose, form, structure, and language",
          "Evaluate writer's style and effectiveness of word choice",
          "Discuss how language is used to achieve author's purpose",
          "Critically analyse literary texts in real-world contexts"
        ],
        keyTerms: [
          { term: "Textual Analysis", definition: "Detailed examination of a text's form, content, language, and meaning." },
          { term: "Contextual Reading", definition: "Understanding a text in relation to its historical, social, or cultural context." },
          { term: "Social Commentary", definition: "Writing that critiques or comments on social issues, behaviours, or systems." },
          { term: "Relevance", definition: "How applicable or meaningful a text's themes are to readers today." }
        ],
        studyNotes: `
## Advanced Textual Analysis

### Layers of Analysis

**1. Surface Level:**
- What happens? (Plot, events)
- Who is involved? (Characters)
- Where and when? (Setting)

**2. Language Level:**
- What words are chosen?
- What figures of speech are used?
- What is the tone?
- How are sentences structured?

**3. Structural Level:**
- How is the text organized?
- What patterns exist?
- How does structure create meaning?

**4. Contextual Level:**
- What influenced the author?
- What historical/social context is relevant?
- How might original audiences have responded?

**5. Interpretive Level:**
- What themes emerge?
- What messages are conveyed?
- How is the text relevant today?

## Connecting Texts to Real World

### Questions to Consider:
1. What social issues does the text address?
2. Are these issues still relevant today?
3. How might readers in Vanuatu relate to the themes?
4. What can we learn from this text?
5. Does the text challenge or reinforce social norms?

### Making Connections:
- **Personal:** How does this relate to your experiences?
- **Local:** How does this apply to Vanuatu society?
- **Global:** How does this connect to world issues?

## Evaluating Literary Effectiveness

### Criteria for Evaluation:
- **Clarity:** Is the message clear?
- **Impact:** Does the text affect readers?
- **Craft:** Are techniques used skillfully?
- **Relevance:** Does it matter?
- **Originality:** Does it offer fresh perspectives?
        `,
        practiceQuestions: [
          "What are the five layers of textual analysis?",
          "Why is contextual reading important for understanding texts?",
          "How can you connect a literary text to real-world issues?",
          "What criteria would you use to evaluate literary effectiveness?",
          "How might historical context affect interpretation of a text?",
          "Explain what 'social commentary' means with an example."
        ],
        examTips: [
          "Move beyond surface-level analysis to deeper interpretation",
          "Always consider the context in which a text was written",
          "Make relevant connections to contemporary issues"
        ]
      }
    ]
  },
  strand3: {
    title: "Writing",
    overview: "Sophisticated writing including analytical essays, formal research reports, and professional documents.",
    image: eslImages.writing,
    units: [
      {
        id: "3.1",
        title: "Research Writing",
        image: eslImages.essay,
        objectives: [
          "Choose and narrow a research topic",
          "Formulate a hypothesis and research questions",
          "Collect and analyse data from multiple sources",
          "Present findings in an appropriate format with proper referencing"
        ],
        keyTerms: [
          { term: "Hypothesis", definition: "A tentative statement or prediction that can be tested through research." },
          { term: "Methodology", definition: "The systematic approach or methods used to collect and analyse data in research." },
          { term: "Primary Source", definition: "Original, firsthand evidence: interviews, surveys, original documents." },
          { term: "Secondary Source", definition: "Analysis or interpretation of primary sources: textbooks, articles, reviews." },
          { term: "In-text Citation", definition: "A brief reference in your writing that indicates where information came from." },
          { term: "Bibliography", definition: "A list of all sources used in research, formatted according to a style guide." }
        ],
        studyNotes: `
## Research Process

### Step 1: Choose and Narrow Topic
- Start with a broad area of interest
- Ask: What specifically interests me?
- Narrow to a focused, manageable topic
- Ensure resources are available

**Example:**
- Broad: Climate change
- Narrowed: Effects of rising sea levels on Vanuatu's coastal communities

### Step 2: Develop Research Questions
- What do I want to find out?
- Why is this important?
- How will I investigate this?

**Types of Questions:**
- Descriptive: What is...? How does...?
- Comparative: How does X compare to Y?
- Cause-effect: What causes...? What effects...?

### Step 3: Formulate Hypothesis
A hypothesis is a prediction you will test.

**Format:** If [condition], then [prediction].

**Example:** "If sea levels continue to rise at the current rate, then coastal communities in Vanuatu will experience increased flooding by 2030."

### Step 4: Collect Data

**Methods:**
- Surveys and questionnaires
- Interviews
- Observation
- Library research
- Online research

**Sources:**
- Primary: Original data you collect
- Secondary: Books, articles, reports

### Step 5: Analyse and Present

**Data Presentation:**
- Tables and charts
- Graphs
- Statistics
- Quotes from interviews

## Referencing

### In-text Citations:
Include author's name and year.
- "According to Smith (2020)..."
- "Research shows that... (Smith, 2020)."

### Bibliography Format:
**Book:**
Author Last Name, First Initial. (Year). *Title*. Publisher.

**Article:**
Author Last Name, First Initial. (Year). Article title. *Journal Name*, Volume(Issue), pages.

**Website:**
Author/Organization. (Year). Title. Retrieved from URL
        `,
        practiceQuestions: [
          "What is the difference between a broad topic and a narrowed research topic?",
          "Write a hypothesis for a study on mobile phone use among students.",
          "What is the difference between primary and secondary sources?",
          "Why is it important to cite your sources?",
          "What are THREE methods for collecting data?",
          "How do you format a bibliography entry for a book?"
        ],
        examTips: [
          "Start with a clear, focused research question",
          "Use both primary and secondary sources",
          "Always cite your sources to avoid plagiarism",
          "Present data clearly using appropriate formats"
        ]
      }
    ]
  },
  strand4: {
    title: "Listening and Speaking",
    overview: "Advanced academic listening and sophisticated oral presentations on complex topics.",
    image: eslImages.speaking,
    units: [
      {
        id: "4.1",
        title: "Academic Presentations",
        image: eslImages.speaking,
        objectives: [
          "Critically analyse key ideas from formal spoken English",
          "Deliver oral presentations with visual aids",
          "Use appropriate communication strategies",
          "Make captivating introductions using various techniques"
        ],
        keyTerms: [
          { term: "Academic Register", definition: "Formal language appropriate for educational and professional settings." },
          { term: "Seminar", definition: "A meeting for discussion and exchange of ideas on a specific topic." },
          { term: "Synopsis", definition: "A brief summary of the main points of something." },
          { term: "Discourse Markers", definition: "Words and phrases that organize and connect ideas in speech: firstly, however, in conclusion." }
        ],
        studyNotes: `
## Academic Presentation Skills

### Planning Your Presentation

**1. Research Thoroughly:**
- Know more than you'll present
- Use credible sources
- Prepare for questions

**2. Structure Clearly:**

| Section | Content | Time |
|---------|---------|------|
| Introduction | Hook, purpose, preview | 10-15% |
| Body | Main points with evidence | 70-75% |
| Conclusion | Summary, final thought | 10-15% |
| Q&A | Answer questions | Varies |

### Opening Techniques

**1. Start with a Question:**
"Have you ever wondered why...?"

**2. Share a Surprising Fact:**
"Did you know that 80% of..."

**3. Tell a Brief Story:**
"Last year, a small village in..."

**4. Use a Quote:**
"As Nelson Mandela once said..."

**5. Present a Problem:**
"Our community faces a serious challenge..."

### Using Visual Aids

**Effective Slides:**
- One main idea per slide
- Use images, diagrams, graphs
- Maximum 6 lines of text
- Large, clear font (24pt minimum)
- Consistent design

**Presenting with Slides:**
- Face your audience, not the screen
- Explain what you're showing
- Don't read slides word-for-word
- Use a pointer or hand gestures
- Practice with your slides

### Academic Vocabulary

**Discourse Markers for Presentations:**

| Function | Markers |
|----------|---------|
| Introducing | Today I will discuss..., The purpose of this presentation is... |
| Sequencing | Firstly, Secondly, Next, Finally |
| Adding | Furthermore, In addition, Moreover |
| Contrasting | However, On the other hand, Nevertheless |
| Cause/Effect | Therefore, Consequently, As a result |
| Concluding | In conclusion, To summarize, In summary |

### Handling Difficult Questions

**Strategies:**
1. Thank the questioner
2. Repeat or paraphrase the question
3. If you don't know, say so honestly
4. Offer to follow up later if needed
5. Keep answers focused and relevant
        `,
        practiceQuestions: [
          "What are FIVE techniques for opening a presentation?",
          "How should you structure an academic presentation?",
          "What makes an effective presentation slide?",
          "What are discourse markers and why are they important?",
          "How should you handle a question you don't know the answer to?",
          "What is the academic register and when should you use it?"
        ],
        examTips: [
          "Practice your presentation multiple times",
          "Use discourse markers to guide your audience",
          "Prepare for likely questions in advance",
          "Keep slides simple and visual"
        ]
      }
    ]
  }
};
